// Floating damage/DNA numbers (monorail-r9y). DOM labels anchored to a world
// position, projected via camera.worldToScreen each frame, drifting up and
// fading out. Same overlay approach as dialog.ts, pooled like orb-beacon.ts.
//
// Frequency discipline (amoeba-game-feel): the beats that fire constantly
// coalesce instead of stacking — DNA gains merge into one growing "+N",
// repeat hits on the same target merge into one climbing number. Damage
// taken is rare enough to get its own label every time.

import type { Camera } from '../engine/camera'
import type { Entity } from '../game/components'
import { T } from '../game/tunables'

export type FloatNumberKind = 'gain' | 'dealt' | 'taken'

interface FloatLabel {
  el: HTMLDivElement
  active: boolean
  kind: FloatNumberKind
  worldX: number
  worldY: number
  age: number
  value: number
  /** Coalesce key: the damaged entity for 'dealt', null otherwise. */
  target: Entity | null
}

export interface FloatNumbers {
  /** Add a DNA gain (+N, gold). Coalesces into the live gain label. */
  gain(worldX: number, worldY: number, amount: number): void
  /** Add damage dealt to a target (sepia ink). Coalesces per target. */
  dealt(target: Entity, worldX: number, worldY: number, amount: number): void
  /** Add damage taken by the player (−N, soft vermillion). */
  taken(worldX: number, worldY: number, amount: number): void
  tick(dt: number, camera: Camera, canvasW: number, canvasH: number): void
}

function formatDamage(amount: number): string {
  const scaled = amount * T.feel.numbers.damageDisplayScale
  return String(Math.max(1, Math.round(scaled)))
}

export function createFloatNumbers(): FloatNumbers {
  const container = document.getElementById('float-numbers') as HTMLDivElement
  const pool: FloatLabel[] = []
  // The single live gain accumulator (eating is the core verb — one label
  // that grows reads calmer than a stream of "+1"s).
  let liveGain: FloatLabel | null = null

  function acquire(kind: FloatNumberKind): FloatLabel {
    let label = pool.find(l => !l.active)
    if (!label) {
      const el = document.createElement('div')
      el.className = 'float-num'
      container.appendChild(el)
      label = { el, active: false, kind, worldX: 0, worldY: 0, age: 0, value: 0, target: null }
      pool.push(label)
    }
    label.active = true
    label.kind = kind
    label.age = 0
    label.value = 0
    label.target = null
    label.el.className = `float-num float-num-${kind}`
    label.el.style.display = 'block'
    return label
  }

  function release(label: FloatLabel): void {
    label.active = false
    label.target = null
    label.el.style.display = 'none'
    if (label === liveGain) liveGain = null
  }

  function render(label: FloatLabel): void {
    label.el.textContent =
      label.kind === 'gain' ? `+${label.value}`
      : label.kind === 'taken' ? `−${formatDamage(label.value)}`
      : formatDamage(label.value)
  }

  return {
    gain(worldX, worldY, amount) {
      if (liveGain && liveGain.active && liveGain.age < T.feel.numbers.gainCoalesceSec) {
        liveGain.value += amount
        liveGain.age = 0
        liveGain.worldX = worldX
        liveGain.worldY = worldY
        render(liveGain)
        return
      }
      const label = acquire('gain')
      label.worldX = worldX
      label.worldY = worldY
      label.value = amount
      liveGain = label
      render(label)
    },

    dealt(target, worldX, worldY, amount) {
      const live = pool.find(
        l => l.active && l.kind === 'dealt' && l.target === target
          && l.age < T.feel.numbers.dealtCoalesceSec,
      )
      if (live) {
        live.value += amount
        live.age = 0
        live.worldX = worldX
        live.worldY = worldY
        render(live)
        return
      }
      const label = acquire('dealt')
      label.target = target
      label.worldX = worldX
      label.worldY = worldY
      label.value = amount
      render(label)
    },

    taken(worldX, worldY, amount) {
      const label = acquire('taken')
      label.worldX = worldX
      label.worldY = worldY
      label.value = amount
      render(label)
    },

    tick(dt, camera, canvasW, canvasH) {
      const life = T.feel.numbers.lifeSec
      for (const label of pool) {
        if (!label.active) continue
        label.age += dt
        if (label.age >= life) {
          release(label)
          continue
        }
        const t = label.age / life
        const screen = camera.worldToScreen(label.worldX, label.worldY)
        // Ease-out rise; fade in the back half so the number lands readable.
        const rise = T.feel.numbers.risePx * (1 - (1 - t) * (1 - t))
        const x = screen.x
        const y = screen.y - rise
        if (x < -40 || x > canvasW + 40 || y < -40 || y > canvasH + 40) {
          label.el.style.opacity = '0'
          continue
        }
        label.el.style.transform = `translate(${x}px, ${y}px) translate(-50%, -100%)`
        label.el.style.opacity = String(t < 0.5 ? 1 : 1 - (t - 0.5) * 2)
      }
    },
  }
}
